/**
 * «Open game» — la quarta tappa del flusso torneo, e la sola in cui il limite conta piu'
 * della funzione.
 *
 * ## 🔴 IL FATTO CHE DECIDE TUTTO IL FILE, e va letto prima del codice
 *
 * ***Limulo non ha una lobby da aprire.*** Misurato il 2026-09-11 (tma1), su
 * `qlash-limulo` a tre ref concordi (HEAD di lavoro, `origin/staging` = `1354b86`,
 * `origin/main` = `8e78c39`):
 *
 *  - La capability per la partita esterna **esiste** — `game.external-match.create`, con la
 *    sua rotta sul BFF (`POST /v1/games/external-matches`, `router.js:1667`, catalogo
 *    `endpoint-catalog.js:624`). ⚠️ Non ha prefisso `tournament.`, ed e' il motivo per cui
 *    un censimento delle sole costanti `TOURNAMENT_*` non la vede.
 *  - **E non restituisce nessuna URL.** `ClashRoyaleProfileProvider.createExternalMatch`
 *    (`app-game-clash-royale/backend/provider.js`) torna `metadata.instructions`: senza
 *    l'endpoint partner Supercell e' `mode: 'manual-friendly-battle'` — istruzioni testuali
 *    piu' i Player Tag; con l'endpoint acceso torna un `remoteMatchTag`, che e' un **tag**,
 *    non un indirizzo. Il provider lo dichiara da se': «Le API pubbliche Clash Royale non
 *    documentano la creazione remota di friendly battle».
 *  - **E l'incontro non porta niente da aprire.** `externalMatch` arriva al client come
 *    chiave (il sanitizer del BFF la emette, `policy.js:484-495`) con valore **`null`**:
 *    `rigaDiIncontro` (`app-qtournaments/app/superficie/capability/incontri.js`) non la
 *    popola mai. ⚠️ Chiave presente e valore nullo e' la forma che somiglia di piu' a «il
 *    dato arrivera'»: chi guarda le chiavi e non i valori conclude il contrario.
 *
 * ⇒ Quindi questo modulo **non apre un incontro: apre il GIOCO**. Ed e' esattamente cio' che
 * fa gia' il ramo Laravel, che qui e' il precedente e non la scorciatoia:
 * `routes/MatchDetail.tsx:99-101` chiama `openLink.ifAvailable('https://link.clashroyale.com')`
 * — una URL **statica**, uguale per ogni incontro di ogni torneo. Anche li' «open game» non e'
 * mai stato «apri questa partita».
 *
 * ⛔ **Chi legge questo file non scriva da nessuna parte «open game verificato» avendo
 * verificato il bottone.** Sono due frasi diverse: che il client Clash Royale si apra davvero
 * non si misura in un browser headless, e la copy a schermo non deve promettere quello che il
 * bottone non puo' mantenere.
 *
 * ## Perche' il gioco si legge dal `profileType` e non dal `gameId`
 *
 * Su staging il torneo dice `gameId: "1"` e `"1"` E' Clash Royale — ma quel numero e' un id
 * **dell'organizzazione**, non un nome del gioco: su un altro tenant `"1"` sarebbe altro.
 * Il campo che porta il gioco e' `profileType` (`GET /v1/games` → `"clash-royale"`), lo stesso
 * che il provider usa come chiave (`CLASH_ROYALE_PROFILE_TYPE`). Cablare il numero
 * significherebbe scrivere nel client un accidente di configurazione travestito da contratto.
 *
 * ⚠️ E se il `profileType` **non arriva** non si indovina: niente bottone, col motivo. Un
 * default su Clash Royale manderebbe un giocatore di un altro gioco ad aprire l'app sbagliata,
 * e nessuno se ne accorgerebbe dallo schermo.
 */

import type { LimuloMatch } from '../../types/limulo.ts';
import {
  LIMULO_TENNIS_CLASH_PROFILE_TYPE,
  piattaformaTennisClashOf,
  tennisClashPartitaOf,
} from './externalData.ts';
import { matchStateOf } from './match.ts';
import { giocoSenzaIntegrazione } from './registration.ts';

/**
 * Perche' il bottone non c'e'. Tre motivi, e sono distinti perche' **portano ad azioni
 * diverse**: `not-playable` e' un fatto sull'incontro (non c'e' niente da giocare),
 * `unknown-game` e' un dato che non e' arrivato (puo' arrivare fra un istante),
 * `unsupported-game` e' un limite nostro dichiarato (non sappiamo dove sta la porta di quel
 * gioco). Collassarli darebbe a tutti e tre la stessa frase, e due su tre sarebbero false.
 *
 * Due in piu' sono di Tennis Clash, dove il link e' della PARTITA e non del gioco, e sono anch'essi
 * distinti perche' portano ad azioni diverse: `phone-only` (da questa piattaforma non si apre — riapri
 * dal telefono) e `no-link` (la piattaforma e' buona ma il link non e' arrivato); e `not-a-player`
 * (chi guarda non e' uno dei due lati: il link e' la partita, e non si da' a chi non la gioca).
 */
export type LimuloOpenGameBlock =
  | 'not-playable'
  | 'unknown-game'
  | 'unsupported-game'
  | 'manual-game'
  | 'phone-only'
  | 'no-link'
  | 'not-a-player';

export interface LimuloOpenGameTarget {
  available: boolean;
  /** L'indirizzo da aprire, o `null`. Mai una stringa vuota: si renderizza come un link rotto. */
  url: string | null;
  block: LimuloOpenGameBlock | null;
  /**
   * `true` = l'indirizzo NON e' http/https (oggi: `tennisclash://…`, il link iOS di Tennis Clash) e a
   * `openLink` non si da' cosi' com'e': Telegram accetta solo http/https (`WebAppTgUrlInvalid`, [M
   * `telegram-web-app.js?57`, 2919-2926]) e la WebView blocca gli schemi personalizzati, quindi passa
   * dalla pagina ponte (`crWatchRedirectUrl`, `public/watch-redirect.html`), che lo inoltra invariato.
   * Assente = si apre com'e' (Clash Royale, e l'https di Android): la chiave non c'e' proprio, cosi' i
   * bersagli di prima restano identici.
   */
  viaPonte?: true;
}

/**
 * Il punto d'ingresso del gioco, per `profileType`.
 *
 * ⛔ **Una voce qui NON e' un link alla partita** — v. il cappello. E' l'indirizzo che apre
 * l'applicazione del gioco sul telefono, lo stesso che il ramo Laravel usa da mesi.
 * ⚠️ Una chiave in piu' qui e' una promessa in piu' a schermo: si aggiunge solo dopo aver
 * misurato che quel gioco ha davvero una porta d'ingresso, e non per simmetria.
 */
export const LIMULO_GAME_ENTRY_POINTS: Readonly<Record<string, string>> = Object.freeze({
  'clash-royale': 'https://link.clashroyale.com',
});

/**
 * Gli stati d'incontro in cui un giocatore ha qualcosa da giocare.
 *
 * `proposed` **e' dentro** e non e' una svista: un referto proposto e non ancora confermato
 * non chiude la partita — chi deve confermare puo' avere ancora bisogno di guardare il gioco
 * per decidere se quel punteggio e' il suo. Gli stati fuori (`bye`, `incomplete`, `reported`,
 * `decided`) sono quelli in cui aprire il gioco non servirebbe a niente, e un bottone che non
 * serve a niente e' un invito a cercare una funzione che non c'e'.
 */
const STATI_GIOCABILI = new Set(['pending', 'proposed']);

/**
 * I giochi il cui link e' della PARTITA (arriva in `externalData`, per piattaforma) e non del gioco.
 * Oggi solo Tennis Clash, e solo con il flag della build acceso (`VITE_FEATURE_TENNIS_CLASH_ENABLED`).
 */
export interface LimuloOpenGameTennisClash {
  /** Il flag della build. Spento: Tennis Clash resta `unsupported-game`, com'era. */
  enabled: boolean;
  /** `tgWebAppPlatform`: `ios`, `android`, `android_x`, o altro (desktop, web, sconosciuta). */
  platform: string | null | undefined;
}

/**
 * Il nome che si dice a schermo per i giochi che aprono con un'etichetta col loro nome
 * («Apri Tennis Clash»). ⚠️ Clash Royale NON e' qui: la sua etichetta e' `actions.openGame.label` e il suo
 * testo resta identico. Una voce in piu' e' un'etichetta in piu': si aggiunge insieme al gioco.
 */
export const LIMULO_OPEN_GAME_NAMES: Readonly<Record<string, string>> = Object.freeze({
  [LIMULO_TENNIS_CLASH_PROFILE_TYPE]: 'Tennis Clash',
});

export function openGameTargetOf(input: {
  match: LimuloMatch;
  /**
   * Il `profileType` del gioco del torneo (`GET /v1/games` → `items[].profileType`).
   * `null`/`undefined` = non e' arrivato, e non si deduce.
   */
  profileType: string | null | undefined;
  /**
   * Solo per Tennis Clash (v. `LimuloOpenGameTennisClash`). Assente = il flag e' spento: nessun
   * chiamante di prima cambia comportamento.
   */
  tennisClash?: LimuloOpenGameTennisClash;
}): LimuloOpenGameTarget {
  // 1. PRIMA lo stato dell'incontro, e non e' un ordine arbitrario: e' il fatto piu' forte.
  //    Su un bye il gioco non si apre nemmeno se il gioco lo conoscessimo benissimo, e dire
  //    «gioco sconosciuto» su un turno passato manderebbe a cercare un dato mancante che non
  //    c'entra niente.
  if (!STATI_GIOCABILI.has(matchStateOf(input.match))) {
    return { available: false, url: null, block: 'not-playable' };
  }

  const tipo = typeof input.profileType === 'string' ? input.profileType.trim() : '';
  // 2. Il dato non e' arrivato. ⛔ Nessun default: v. il cappello.
  if (tipo === '') return { available: false, url: null, block: 'unknown-game' };

  // 3. Un gioco SENZA integrazione (`generic`, o un profilo dedicato come `efootball`: I-201,
  //    `L-2270`; v. `LIMULO_PROFILE_TYPES_SENZA_INTEGRAZIONE`). Non e' un limite
  //    nostro come il caso sotto: non c'e' niente da aprire per costruzione, e la partita si
  //    gioca nel gioco e si referta qui (ADR-0138). Dirlo «non sappiamo aprirlo» farebbe
  //    cercare una porta che non esiste.
  if (giocoSenzaIntegrazione(tipo)) return { available: false, url: null, block: 'manual-game' };

  // 3-bis. Tennis Clash: il link e' della PARTITA, lo da' il provider, e cambia con la piattaforma.
  //    ⛔ Solo col flag della build: spento, cade sul ramo 4 come prima. ⛔ Il client non costruisce mai il
  //    link (ADR-0157 §7): se per questa piattaforma non c'e', niente bottone e il motivo.
  if (tipo === LIMULO_TENNIS_CLASH_PROFILE_TYPE && input.tennisClash?.enabled === true) {
    // ⛔ Il link e' la PARTITA («both players open the same link to enter the private match»,
    //    `provider.js:133-135`): lo si da' solo a chi la gioca. Il BFF manda `externalData` anche allo staff
    //    owner/admin, e uno di loro che tocca il bottone entrerebbe nella stanza di altri due. Stessa regola di
    //    `comeSiGiocaVisibile` e `chatVisibile`: `isMe === true` su un lato, e `isMe` ASSENTE non e' `true`.
    if (input.match.players.a?.isMe !== true && input.match.players.b?.isMe !== true) {
      return { available: false, url: null, block: 'not-a-player' };
    }
    const piattaforma = piattaformaTennisClashOf(input.tennisClash.platform);
    if (piattaforma === null) return { available: false, url: null, block: 'phone-only' };
    const partita = tennisClashPartitaOf({ externalData: input.match.externalData, profileType: tipo });
    const link = piattaforma === 'ios' ? partita?.deepLinkIos : partita?.deepLinkAndroid;
    if (!link) return { available: false, url: null, block: 'no-link' };
    // `openLink` riceve solo http/https: uno schema personalizzato passa dal ponte (v. `viaPonte`).
    return /^https?:/i.test(link)
      ? { available: true, url: link, block: null }
      : { available: true, url: link, block: null, viaPonte: true };
  }

  // 4. Gioco noto al server, porta d'ingresso sconosciuta a noi. E' un limite NOSTRO, e si
  //    dichiara come tale invece di far sembrare che l'incontro abbia qualcosa che non va.
  // ⚠️ `Object.hasOwn`, non l'indice nudo (revisione di #424): il `profileType` arriva dal catalogo del
  // server, e su un oggetto `LIMULO_GAME_ENTRY_POINTS['constructor']` rende la funzione `Object` — cioe'
  // un gioco «apribile» con un URL che non e' un URL.
  const url = Object.hasOwn(LIMULO_GAME_ENTRY_POINTS, tipo) ? LIMULO_GAME_ENTRY_POINTS[tipo] : undefined;
  if (url === undefined) return { available: false, url: null, block: 'unsupported-game' };

  return { available: true, url, block: null };
}
